// @flow

export const GRAB_POINT = 'GRAB-POINT';
export const FLOW_EARN = 'earn';

// A Uno major unit (e.g. 1 THC) is made up of 100 minor units. The currency
// config API returns `unoToFiatCurrencyMinorUnits` per Uno *minor* unit, so this
// factor is used to derive the fiat value of one whole Uno (major) unit.
const UNO_MINOR_UNITS_PER_MAJOR = 100;

// Conversion rates are not always exactly representable in floating point, so a
// raw modulo can leave a tiny remainder and wrongly flag valid amounts. Check
// divisibility via the quotient against a small tolerance instead.
const EPSILON = 1e-9;

// The currency config API does not return a fiat exponent, so keep a small map
// of fiat currency -> decimal places (minor units per major = 10^decimals) for
// the rolled-out markets. Unknown currencies fall back to 2 decimals.
const DEFAULT_FIAT_DECIMALS = 2;
const FIAT_DECIMALS = {
  THB: 2,
  SGD: 2,
  MYR: 2,
  PHP: 2,
  KHR: 2,
  MMK: 2,
  VND: 0,
  IDR: 0,
};

export type CurrencyConfig = {
  country: string,
  pointCurrency: string,
  fiatCurrency: string,
  exponent: number,
  // int64 fields are serialised as strings over the gateway JSON.
  earnBlockSizeInGRP: number | string,
  burnBlockSizeInGRP: number | string,
  // double fields are serialised as numbers.
  unoToGrpMinorUnits: number,
  unoToFiatCurrencyMinorUnits: number,
};

export function isDivisibleBy(value: number, step: number | string): boolean {
  const stepNum = Number(step);
  if (!stepNum) return true;
  const quotient = value / stepNum;
  return Math.abs(quotient - Math.round(quotient)) < EPSILON;
}

// Config can list multiple rolled-out countries; the parent decides which one
// to surface by passing its country code. Returns null when the country is not
// a rolled-out Uno country (or none supplied), so the caller renders nothing.
export function selectConfigForCountry(
  configs: ?Array<CurrencyConfig>,
  country: ?string
): ?CurrencyConfig {
  if (!configs || !country) return null;
  return configs.find(config => config.country === country) || null;
}

// GrabCoins block size (in GRP minor units) that the amount must be a multiple
// of for the given flow.
export function blockSizeFor(config: CurrencyConfig, flowType: string): number {
  return Number(flowType === FLOW_EARN ? config.earnBlockSizeInGRP : config.burnBlockSizeInGRP);
}

// The API returns `unoToGrpMinorUnits` as GRP per Uno *minor* unit (e.g. 0.2 for
// TH). One Uno major unit (1 THC) is 100 minor units, so the GRP value of a whole
// GrabCoin is `unoToGrpMinorUnits * 100` (e.g. 0.2 * 100 = 20 GRP per THC).
export function getGrpPerUnoMajor(config: CurrencyConfig): number {
  return Number(config.unoToGrpMinorUnits) * UNO_MINOR_UNITS_PER_MAJOR;
}

// Normalise the Ops-configured amount into a GRP (GrabCoins) amount. When the
// configured currency is a Uno point currency, convert its major amount back to
// GRP using the matching entry's rate; otherwise the amount is already in GRP.
// Returns null when `fromCurrency` is neither GRAB_POINT nor a currency with a
// known rate in `configs` (e.g. a point currency not yet rolled out to Uno) —
// callers must not fall back to treating an unrecognised currency as GRP.
export function toGrpAmount(
  amount: number,
  fromCurrency: string,
  configs: Array<CurrencyConfig>
): ?number {
  if (fromCurrency === GRAB_POINT) return amount;
  const match = configs.find(config => config.pointCurrency === fromCurrency);
  if (!match) return null;
  return amount * getGrpPerUnoMajor(match);
}

// GrabCoins shown to a migrated user = the amount expressed in Uno major units,
// e.g. 202 GRP / 20 => 10.1 (THC), displayed to the config's decimal precision.
export function getMigratedDisplayAmount(config: CurrencyConfig, grpAmount: number): string {
  return (grpAmount / getGrpPerUnoMajor(config)).toFixed(config.exponent);
}

export function formatAmount(value: number): string {
  if (!Number.isFinite(value)) return '';
  return String(Number(value.toFixed(4)));
}

// Un-rounded migrated amount, so an invalid value's offending decimals are
// visible (e.g. 203 GRP / 20 => "10.15" rather than the display-rounded "10.2").
export function getMigratedRawAmount(config: CurrencyConfig, grpAmount: number): string {
  return formatAmount(grpAmount / getGrpPerUnoMajor(config));
}

// Granularity the migrated amount must land on, in Uno major units, e.g. burn
// TH => 20 / 20 = 1 (whole numbers); earn TH => 2 / 20 = 0.1 (one decimal).
export function getMigratedStepMajor(config: CurrencyConfig, flowType: string): number {
  return blockSizeFor(config, flowType) / getGrpPerUnoMajor(config);
}

// If `step` is a power of ten (1, 0.1, 0.01…), return its decimal places
// (0, 1, 2…); otherwise null (e.g. a 0.2 step is not a decimal-place boundary).
export function powerOfTenDecimals(step: number): ?number {
  if (!(step > 0)) return null;
  // `|| 0` normalises the -0 that Math.round(-Math.log10(1)) produces.
  const decimals = Math.round(-Math.log10(step)) || 0;
  if (decimals < 0) return null;
  return Math.abs(step - (10 ** -decimals)) < EPSILON ? decimals : null;
}

// Fiat value (in major units) of one whole Uno unit, e.g. 1 THC = 1 THB.
export function getFiatMajorPerUno(config: CurrencyConfig): string {
  const decimals = FIAT_DECIMALS[config.fiatCurrency] != null
    ? FIAT_DECIMALS[config.fiatCurrency]
    : DEFAULT_FIAT_DECIMALS;
  const fiatMinorPerUno = Number(config.unoToFiatCurrencyMinorUnits) * UNO_MINOR_UNITS_PER_MAJOR;
  return formatAmount(fiatMinorPerUno / (10 ** decimals));
}
